import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { handlePlainTextCopy } from "@/lib/plainTextCopy";
import { canDo } from "@shared/roles";
import {
  useSavDossiers, useSavDossier, useCreateSavDossier, useUpdateSavDossier, useDeleteSavDossier,
  useSavInterventions, useCreateSavIntervention, useUpdateSavIntervention, useDeleteSavIntervention,
  useSavPieces, useCreateSavPiece, useUpdateSavPiece, useDeleteSavPiece,
} from "@/hooks/use-sav";
import { useTechnicians, useProfiles } from "@/hooks/use-profiles";
import { useClients } from "@/hooks/use-clients";
import { useRobotsClients, useRobotsClientsOrphelins } from "@/hooks/use-robots-clients";
import { useQuery } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { queryClient } from "@/lib/queryClient";
import { extractLocation } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/status-badge";
import { SearchSelect } from "@/components/search-select";
import {
  Plus, Search, Wrench, Eye, Pencil, Trash2, Clock, Camera, Package, MessageSquare, ArrowLeft, Upload, Mic,
  Settings2, ChevronDown, ChevronUp, CheckCircle2, ClipboardList, Bot, Sparkles, Copy, RefreshCw, Loader2, X, FileSearch
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import type { SavDossier, SavIntervention, SavPiece, Robot, RobotClient, Product, Profile } from "@shared/schema";
import { isRobotFleetAvailable, ROBOT_FLEET_STATUS_VALUES } from "@shared/robotStatusConfig";

const prioriteLabels: Record<string, string> = { faible: "Faible", moyenne: "Moyenne", elevee: "Élevée" };
const prioriteColors: Record<string, string> = {
  faible: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  moyenne: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
  elevee: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
};
const statutLabels: Record<string, string> = {
  ouvert: "Ouvert", en_cours: "En cours", en_attente_pieces: "En attente pièces", resolu: "Résolu", cloture: "Clôturé",
};
const pieceStatutLabels: Record<string, string> = {
  a_commander: "À commander", commandee: "Commandée", recue: "Reçue", posee: "Posée",
};
type SavDossierDisplay = SavDossier & { robotClient?: RobotClient | null; suiviCount?: number };
const EDITED_PREFIX = '\u2063[modifié]\u2063';
function savRobotLabel(d: SavDossierDisplay, robots: Robot[]) {
  const robot = robots.find(r => r.id === d.robotId);
  if (robot) return `${robot.modelName} — ${robot.serialNumber}`;
  if (d.robotClient) return `${d.robotClient.modele} — ${d.robotClient.numeroSerie}`;
  const ext = d.robotExterne as { modele?: string; numero_serie?: string } | null;
  return ext?.modele || ext?.numero_serie ? `${ext.modele || 'Robot externe'} — ${ext.numero_serie || 'S/N inconnu'}` : '—';
}

function addDays(dateStr: string, days: number) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

const emptyForm = {
  origine: "client" as string,
  robotId: "",
  robotClientId: "",
  robotExterne: { modele: "", numero_serie: "", mac_address: "" },
  clientId: "",
  garantie: false,
  problematique: "",
  priorite: "moyenne" as string,
  dateCreation: new Date().toISOString().split('T')[0],
  dateFinPrevisionnelle: addDays(new Date().toISOString().split('T')[0], 15),
  technicienReferentId: "",
  notesGenerales: "",
  numeroSuiviTransporteur: "",
  retraitNecessaire: false,
  deplacementTechnicien: false,
  robotRemplacementId: "",
  technicienPartenaire: false,
};

export default function SavList() {
  const { profile } = useAuth();
  const { data: dossiers, isLoading } = useSavDossiers();
  const { data: techniciansList } = useTechnicians();
  const { data: clientsList } = useClients();
  const { data: robotsList } = useQuery<Robot[]>({
    queryKey: ['/api/robots'],
    queryFn: async () => { const r = await authFetch('/api/robots'); return r.json(); },
  });
  const { data: productsList } = useQuery<Product[]>({
    queryKey: ['/api/products'],
    queryFn: async () => { const r = await authFetch('/api/products'); return r.json(); },
  });
  const { data: orphelins = [] } = useRobotsClientsOrphelins();
  const { toast } = useToast();

  const technicians = techniciansList || [];
  const clients = clientsList || [];
  const robots = robotsList || [];
  const products = productsList || [];

  const [formData, setFormData] = useState({ ...emptyForm });
  const { data: clientRobotsList } = useRobotsClients(formData.origine === 'client' && formData.clientId ? formData.clientId : null);
  const clientRobots = clientRobotsList || [];

  const [search, setSearch] = useState("");
  const [filterStatut, setFilterStatut] = useState("all");
  const [filterPriorite, setFilterPriorite] = useState("all");
  const [filterTech, setFilterTech] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [detailDossierId, setDetailDossierId] = useState<string | null>(null);
  const [showProcessCreate, setShowProcessCreate] = useState(false);

  // SN search state
  const [snQuery, setSnQuery] = useState("");
  const [snDebouncedQuery, setSnDebouncedQuery] = useState("");
  const [puduFicheLoading, setPuduFicheLoading] = useState(false);

  const createMutation = useCreateSavDossier();

  // Debounce SN search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSnDebouncedQuery(snQuery.trim().length >= 3 ? snQuery.trim() : "");
    }, 300);
    return () => clearTimeout(timer);
  }, [snQuery]);

  // URL param pre-fill (from robots/list.tsx SAV tab)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('prefill') === '1') {
      const newForm = { ...emptyForm };
      const origine = params.get('origine');
      if (origine) newForm.origine = origine;
      const robotId = params.get('robotId');
      if (robotId) newForm.robotId = robotId;
      const clientId = params.get('clientId');
      if (clientId) newForm.clientId = clientId;
      const robotClientId = params.get('robotClientId');
      if (robotClientId) newForm.robotClientId = robotClientId;
      const sn = params.get('sn');
      const model = params.get('model');
      if (sn || model) {
        newForm.robotExterne = {
          modele: model || '',
          numero_serie: sn || '',
          mac_address: params.get('mac') || '',
        };
      }
      setFormData(newForm);
      setCreateOpen(true);
      window.history.replaceState(null, '', '/sav');
    }
  }, []);

  // SN search query
  const { data: snResults = [], isLoading: snLoading } = useQuery<any[]>({
    queryKey: ['/api/sav/recherche-robots', snDebouncedQuery],
    queryFn: async () => {
      if (!snDebouncedQuery) return [];
      const r = await authFetch(`/api/sav/recherche-robots?sn=${encodeURIComponent(snDebouncedQuery)}`);
      if (!r.ok) throw new Error(`Erreur serveur ${r.status}`);
      const data = await r.json();
      return Array.isArray(data) ? data : [];
    },
    enabled: snDebouncedQuery.length >= 3,
  });

  const handlePrefillFromSN = (result: any) => {
    const newForm = { ...emptyForm };
    if (result.origine === 'client' && result.robots_clients_id) {
      newForm.origine = 'client';
      if (result.client?.id) newForm.clientId = result.client.id;
      newForm.robotClientId = result.robots_clients_id;
    } else if (result.origine === 'parc_jobotto' && result.robots_id) {
      newForm.origine = 'demo';
      newForm.robotId = result.robots_id;
    } else {
      newForm.origine = 'client';
      newForm.robotExterne = {
        modele: result.modele || '',
        numero_serie: result.sn,
        mac_address: result.mac || '',
      };
      if (result.client?.id) newForm.clientId = result.client.id;
    }
    setFormData(newForm);
    setSnQuery('');
    setCreateOpen(true);
  };

  const handleCreerFiche = async (result: any) => {
    setPuduFicheLoading(true);
    try {
      const r = await authFetch('/api/robots-clients/depuis-pudu', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sn: result.sn }),
      });
      const created = await r.json();
      if (!r.ok) throw new Error(created.message || 'Erreur création');
      queryClient.invalidateQueries({ queryKey: ['/api/clients'] });
      const newForm = { ...emptyForm, origine: 'client' as string, robotClientId: created.id };
      if (created.clientId) newForm.clientId = created.clientId;
      setFormData(newForm);
      setSnQuery('');
      setCreateOpen(true);
      toast({ title: 'Fiche créée', description: `Robot ${result.sn} ajouté dans les robots clients` });
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Erreur', description: err.message });
    } finally {
      setPuduFicheLoading(false);
    }
  };

  const canEdit = canDo(profile?.accessRole) || profile?.role === 'planification' || profile?.role === 'technicien';

  const filtered = dossiers?.filter((d: SavDossierDisplay) => {
    if (filterStatut !== "all" && d.statut !== filterStatut) return false;
    if (filterPriorite !== "all" && d.priorite !== filterPriorite) return false;
    if (filterTech !== "all" && d.technicienReferentId !== filterTech) return false;
    if (search) {
      const s = search.toLowerCase();
      const client = clients.find(c => c.id === d.clientId);
      return d.reference.toLowerCase().includes(s) ||
        savRobotLabel(d, robots).toLowerCase().includes(s) ||
        ((client as any)?.nomCommercial || client?.companyName || "").toLowerCase().includes(s) ||
        d.problematique.toLowerCase().includes(s) ||
        (d.notesGenerales || '').toLowerCase().includes(s);
    }
    return true;
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: any = {
        origine: formData.origine,
        garantie: formData.garantie,
        problematique: formData.problematique,
        priorite: formData.priorite,
        dateCreation: formData.dateCreation,
        dateFinPrevisionnelle: formData.dateFinPrevisionnelle,
        notesGenerales: formData.notesGenerales || null,
        numeroSuiviTransporteur: formData.numeroSuiviTransporteur || null,
        technicienReferentId: formData.technicienReferentId || null,
        clientId: formData.clientId || null,
      };
      if (formData.origine === 'demo' && formData.robotId) {
        payload.robotId = formData.robotId;
      } else if (formData.origine === 'client') {
        if (formData.robotClientId) {
          payload.robotClientId = formData.robotClientId;
        }
        if (formData.robotExterne.modele || formData.robotExterne.numero_serie) {
          payload.robotExterne = formData.robotExterne;
        }
        if (formData.robotId) payload.robotId = formData.robotId;
      }
      payload.retraitNecessaire = formData.retraitNecessaire;
      payload.deplacementTechnicien = formData.deplacementTechnicien;
      payload.technicienPartenaire = formData.technicienPartenaire;
      if (formData.robotRemplacementId) payload.robotRemplacementId = formData.robotRemplacementId;
      await createMutation.mutateAsync(payload);
      setCreateOpen(false);
      setFormData({ ...emptyForm });
      setShowProcessCreate(false);
      toast({ title: "Dossier SAV créé" });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    }
  };

  if (detailDossierId) {
    return <SavDetail
      dossierId={detailDossierId}
      onBack={() => setDetailDossierId(null)}
      technicians={technicians}
      clients={clients}
      robots={robots}
      products={products}
      canEdit={canEdit}
    />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">SAV</h1>
          <p className="text-muted-foreground mt-1">Gestion des dossiers de réparation</p>
        </div>
        {canEdit && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="button-create-sav"><Plus className="w-4 h-4 mr-2" />Nouveau dossier</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[700px]">
              <DialogHeader>
                <DialogTitle>Nouveau dossier SAV</DialogTitle>
                <DialogDescription>Créer un dossier de réparation</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 pt-4 max-h-[70vh] overflow-y-auto pr-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Origine *</Label>
                    <Select value={formData.origine} onValueChange={v => setFormData({ ...formData, origine: v, robotId: "", robotClientId: "", robotExterne: { modele: "", numero_serie: "", mac_address: "" } })}>
                      <SelectTrigger data-testid="select-origine"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="client">Robot client</SelectItem>
                        <SelectItem value="demo">Robot démo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Priorité</Label>
                    <Select value={formData.priorite} onValueChange={v => setFormData({ ...formData, priorite: v })}>
                      <SelectTrigger data-testid="select-priorite"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="faible">Faible</SelectItem>
                        <SelectItem value="moyenne">Moyenne</SelectItem>
                        <SelectItem value="elevee">Élevée</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {formData.origine === 'demo' ? (
                  <div className="space-y-2">
                    <Label>Robot du référentiel *</Label>
                    <SearchSelect
                      value={formData.robotId || null}
                      onValueChange={v => setFormData({ ...formData, robotId: v || "" })}
                      options={robots.filter(r => r.robotType === 'demo' && r.status !== ROBOT_FLEET_STATUS_VALUES.EN_REPARATION).map(r => ({
                        value: r.id,
                        label: r.modelName,
                        sublabel: [r.serialNumber, r.nickname].filter(Boolean).join(" · "),
                      }))}
                      placeholder="Sélectionner un robot"
                      emptyText="Aucun robot trouvé"
                      data-testid="select-robot"
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {formData.clientId && clientRobots.length > 0 && (
                      <div className="space-y-2">
                        <Label>Robot du client</Label>
                        <SearchSelect
                          value={formData.robotClientId || null}
                          onValueChange={v => setFormData({ ...formData, robotClientId: v || "" })}
                          options={clientRobots.map(r => ({
                            value: r.id,
                            label: r.modele,
                            sublabel: [r.numeroSerie, r.macAddress].filter(Boolean).join(" · "),
                          }))}
                          placeholder="Sélectionner un robot du client"
                          emptyText="Aucun robot trouvé"
                          data-testid="select-robot-client"
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Robot du référentiel (optionnel)</Label>
                      <SearchSelect
                        value={formData.robotId || null}
                        onValueChange={v => setFormData({ ...formData, robotId: v || "" })}
                        options={robots.map(r => ({
                          value: r.id,
                          label: r.modelName,
                          sublabel: [r.serialNumber, r.nickname].filter(Boolean).join(" · "),
                        }))}
                        placeholder="Sélectionner si dans le référentiel"
                        emptyText="Aucun robot trouvé"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Modèle robot externe</Label>
                        <Input value={formData.robotExterne.modele} onChange={e => setFormData({ ...formData, robotExterne: { ...formData.robotExterne, modele: e.target.value } })} placeholder="Ex: BellaBot" />
                      </div>
                      <div className="space-y-2">
                        <Label>N° série</Label>
                        <Input value={formData.robotExterne.numero_serie} onChange={e => setFormData({ ...formData, robotExterne: { ...formData.robotExterne, numero_serie: e.target.value } })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Adresse MAC</Label>
                        <Input value={formData.robotExterne.mac_address} onChange={e => setFormData({ ...formData, robotExterne: { ...formData.robotExterne, mac_address: e.target.value } })} />
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Client</Label>
                    <SearchSelect
                      value={formData.clientId || null}
                      onValueChange={v => setFormData({ ...formData, clientId: v || "" })}
                      options={clients.map(c => ({
                        value: c.id,
                        label: (c as any).nomCommercial || c.companyName,
                        sublabel: extractLocation(c.address),
                      }))}
                      placeholder="Sélectionner"
                      emptyText="Aucun client trouvé"
                      data-testid="select-client"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Technicien référent</Label>
                    <SearchSelect
                      value={formData.technicienReferentId || null}
                      onValueChange={v => setFormData({ ...formData, technicienReferentId: v || "" })}
                      options={technicians.map(t => ({
                        value: t.id,
                        label: `${t.firstName} ${t.lastName}`,
                      }))}
                      placeholder="Sélectionner"
                      emptyText="Aucun technicien trouvé"
                      data-testid="select-tech"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Problématique *</Label>
                  <Textarea data-testid="input-problematique" required value={formData.problematique} onChange={e => setFormData({ ...formData, problematique: e.target.value })} placeholder="Décrire le problème rencontré..." />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date de création</Label>
                    <Input type="date" value={formData.dateCreation} onChange={e => {
                      setFormData({ ...formData, dateCreation: e.target.value, dateFinPrevisionnelle: addDays(e.target.value, 15) });
                    }} />
                  </div>
                  <div className="space-y-2">
                    <Label>Date fin prévisionnelle</Label>
                    <Input type="date" value={formData.dateFinPrevisionnelle} onChange={e => setFormData({ ...formData, dateFinPrevisionnelle: e.target.value })} />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox id="garantie" checked={formData.garantie} onCheckedChange={c => setFormData({ ...formData, garantie: c === true })} />
                  <Label htmlFor="garantie" className="cursor-pointer">Sous garantie</Label>
                </div>

                <div className="space-y-2">
                  <Label>N° suivi transporteur</Label>
                  <Input value={formData.numeroSuiviTransporteur} onChange={e => setFormData({ ...formData, numeroSuiviTransporteur: e.target.value })} placeholder="Ex: COLISSIMO 1234567890" />
                </div>

                <div className="space-y-2">
                  <Label>Notes générales</Label>
                  <Textarea value={formData.notesGenerales} onChange={e => setFormData({ ...formData, notesGenerales: e.target.value })} />
                </div>

                {/* Process de traitement */}
                <div className="border rounded-lg overflow-hidden">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-4 py-3 bg-muted/40 hover:bg-muted/60 transition-colors text-sm font-medium"
                    onClick={() => setShowProcessCreate(!showProcessCreate)}
                  >
                    <span className="flex items-center gap-2"><Settings2 className="w-4 h-4" />Process de traitement</span>
                    {showProcessCreate ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {showProcessCreate && (
                    <div className="p-4 space-y-3 border-t">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-center gap-2">
                          <Checkbox id="c-retrait" checked={formData.retraitNecessaire} onCheckedChange={c => setFormData({ ...formData, retraitNecessaire: c === true })} />
                          <Label htmlFor="c-retrait" className="cursor-pointer text-sm">Retrait nécessaire</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox id="c-deplacement" checked={formData.deplacementTechnicien} onCheckedChange={c => setFormData({ ...formData, deplacementTechnicien: c === true })} />
                          <Label htmlFor="c-deplacement" className="cursor-pointer text-sm">Déplacement technicien</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox id="c-partenaire" checked={formData.technicienPartenaire} onCheckedChange={c => setFormData({ ...formData, technicienPartenaire: c === true })} />
                          <Label htmlFor="c-partenaire" className="cursor-pointer text-sm">Technicien partenaire</Label>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm">Robot de remplacement</Label>
                        <SearchSelect
                          value={formData.robotRemplacementId || null}
                          onValueChange={v => setFormData({ ...formData, robotRemplacementId: v || "" })}
                          options={robots.filter(r => r.robotType === 'demo' && isRobotFleetAvailable(r.status)).map(r => ({
                            value: r.id,
                            label: r.modelName,
                            sublabel: [r.serialNumber, r.nickname].filter(Boolean).join(" · "),
                          }))}
                          placeholder="Aucun robot de remplacement"
                          emptyText="Aucun robot disponible"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="pt-4 flex justify-end gap-2">
                  <Button variant="outline" type="button" onClick={() => setCreateOpen(false)}>Annuler</Button>
                  <Button type="submit" disabled={createMutation.isPending} data-testid="button-submit-sav">
                    {createMutation.isPending ? "Création..." : "Créer le dossier"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Worklist : fiches robots à compléter (admin/planification) */}
      {orphelins.length > 0 && (canDo(profile?.accessRole) || profile?.role === 'planification') && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-700 shadow-sm overflow-hidden" data-testid="worklist-a-completer">
          <div className="p-3 border-b border-amber-300 dark:border-amber-700 bg-amber-100/60 dark:bg-amber-900/20 flex items-center gap-2">
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-200 text-amber-800 dark:bg-amber-800/60 dark:text-amber-300" data-testid="worklist-count-badge">{orphelins.length}</span>
            <span className="text-sm font-medium text-amber-800 dark:text-amber-300">Fiches robots à compléter</span>
            <span className="text-xs text-amber-700 dark:text-amber-400">— créées depuis Pudu sans propriétaire attribué</span>
          </div>
          <div className="divide-y divide-amber-200 dark:divide-amber-800/40 max-h-48 overflow-y-auto">
            {orphelins.map((rc) => (
              <div key={rc.id} className="px-3 py-2 flex items-center justify-between gap-2" data-testid={`worklist-row-${rc.id}`}>
                <div className="flex flex-col min-w-0">
                  <span className="font-mono text-xs font-semibold text-amber-900 dark:text-amber-200 truncate">{rc.numeroSerie}</span>
                  <span className="text-xs text-amber-700 dark:text-amber-400">{rc.modele || '—'}</span>
                </div>
                <span className="text-xs text-amber-600 dark:text-amber-500 shrink-0">{rc.createdAt ? new Date(rc.createdAt).toLocaleDateString('fr-FR') : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recherche SN multi-table */}
      <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
        <div className="p-3 border-b bg-secondary/20">
          <div className="flex items-center gap-2">
            <FileSearch className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="text-sm font-medium">Recherche par numéro de série</span>
            <span className="text-xs text-muted-foreground">(tous robots : clients, parc, Pudu)</span>
          </div>
        </div>
        <div className="p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              data-testid="input-sn-search"
              className="pl-10 pr-8"
              placeholder="Ex : 8S2034919050013 (min 3 caractères)…"
              value={snQuery}
              onChange={e => setSnQuery(e.target.value)}
            />
            {snQuery && (
              <button
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5 rounded"
                onClick={() => { setSnQuery(''); setSnDebouncedQuery(''); }}
                data-testid="button-clear-sn"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        {snDebouncedQuery && (
          <div className="border-t divide-y max-h-80 overflow-y-auto">
            {snLoading ? (
              <div className="p-4 text-sm text-muted-foreground text-center flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />Recherche en cours…
              </div>
            ) : snResults.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground text-center">
                Aucun robot trouvé pour « {snDebouncedQuery} »
              </div>
            ) : snResults.map((r: any) => (
              <div key={r.sn} className="p-3 flex items-center justify-between gap-3 hover:bg-secondary/20 transition-colors" data-testid={`sn-result-${r.sn}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-semibold">{r.sn}</span>
                    {r.modele && <span className="text-xs text-muted-foreground">{r.modele}</span>}
                    {r.mac && <span className="text-xs text-muted-foreground font-mono hidden sm:inline">{r.mac}</span>}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    {r.present_dans.robots_clients && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400" data-testid={`badge-client-${r.sn}`}>Client</span>
                    )}
                    {r.present_dans.robots && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" data-testid={`badge-parc-${r.sn}`}>Parc Jobotto</span>
                    )}
                    {r.present_dans.pudu_robots && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400" data-testid={`badge-pudu-${r.sn}`}>Pudu</span>
                    )}
                    {(r as any).a_completer && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" data-testid={`badge-a-completer-${r.sn}`}>À compléter</span>
                    )}
                    {r.client && <span className="text-xs text-muted-foreground">— {r.client.nom}</span>}
                    {!r.client && r.pudu?.shop_name && <span className="text-xs text-muted-foreground">— {r.pudu.shop_name}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {r.origine === 'pudu' && (
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`button-creer-fiche-${r.sn}`}
                      onClick={() => handleCreerFiche(r)}
                      disabled={puduFicheLoading}
                    >
                      {puduFicheLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Bot className="w-3 h-3 mr-1" />}
                      Créer la fiche
                    </Button>
                  )}
                  {canEdit && (
                    <Button
                      size="sm"
                      data-testid={`button-creer-sav-${r.sn}`}
                      onClick={() => handlePrefillFromSN(r)}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Dossier SAV
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input data-testid="input-search" aria-label="Filtrer les dossiers SAV" className="pl-10" placeholder="Filtrer les dossiers : référence, robot, S/N, client, notes…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Select value={filterStatut} onValueChange={setFilterStatut}>
          <SelectTrigger className="w-[160px]" data-testid="filter-statut"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(statutLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterPriorite} onValueChange={setFilterPriorite}>
          <SelectTrigger className="w-[140px]" data-testid="filter-priorite"><SelectValue placeholder="Priorité" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les priorités</SelectItem>
            {Object.entries(prioriteLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterTech} onValueChange={setFilterTech}>
          <SelectTrigger className="w-[180px]" data-testid="filter-tech"><SelectValue placeholder="Technicien" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les techniciens</SelectItem>
            {technicians.map(t => <SelectItem key={t.id} value={t.id}>{t.firstName} {t.lastName}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="text-center py-10 text-muted-foreground">Chargement...</div>
      ) : !filtered?.length ? (
        <div className="text-center py-10 text-muted-foreground">
          <Wrench className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p>Aucun dossier SAV</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Référence</TableHead>
                <TableHead>Robot</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Priorité</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Technicien</TableHead>
                <TableHead>Création</TableHead>
                <TableHead>Fin prév.</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((d: SavDossierDisplay) => {
                const client = clients.find(c => c.id === d.clientId);
                const tech = technicians.find(t => t.id === d.technicienReferentId);
                return (
                  <TableRow key={d.id} className={`cursor-pointer hover:bg-muted/50 ${(d as any).isMine ? "border-l-[3px] border-l-[hsl(var(--accent-mine))]" : ""}`} data-testid={`row-sav-${d.id}`}>
                    <TableCell className="font-mono font-semibold text-primary" onClick={() => setDetailDossierId(d.id)}>
                      {d.reference}
                      {!!(d.suiviCount || d.notesGenerales?.trim()) && <span className="ml-2 inline-flex items-center gap-1 text-xs text-muted-foreground" title={`${d.suiviCount || 0} entrée(s) de suivi${d.notesGenerales?.trim() ? ' · notes générales' : ''}`}><MessageSquare className="w-3 h-3" />{d.suiviCount || 0}</span>}
                    </TableCell>
                    <TableCell onClick={() => setDetailDossierId(d.id)}>
                      {savRobotLabel(d, robots)}
                    </TableCell>
                    <TableCell onClick={() => setDetailDossierId(d.id)}>{(client as any)?.nomCommercial || client?.companyName || '—'}</TableCell>
                    <TableCell onClick={() => setDetailDossierId(d.id)}>
                      <Badge className={prioriteColors[d.priorite]}>{prioriteLabels[d.priorite]}</Badge>
                    </TableCell>
                    <TableCell onClick={() => setDetailDossierId(d.id)}>
                      <StatusBadge status={d.statut} type="ticket" label={statutLabels[d.statut]} />
                    </TableCell>
                    <TableCell onClick={() => setDetailDossierId(d.id)}>{tech ? `${tech.firstName} ${tech.lastName}` : '—'}</TableCell>
                    <TableCell onClick={() => setDetailDossierId(d.id)}>{d.dateCreation}</TableCell>
                    <TableCell onClick={() => setDetailDossierId(d.id)}>{d.dateFinPrevisionnelle || '—'}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => setDetailDossierId(d.id)} data-testid={`button-view-sav-${d.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function SavDetail({
  dossierId, onBack, technicians, clients, robots, products, canEdit
}: {
  dossierId: string;
  onBack: () => void;
  technicians: Profile[];
  clients: any[];
  robots: Robot[];
  products: Product[];
  canEdit: boolean;
}) {
  const { data: dossier, isLoading } = useSavDossier(dossierId);
  const { data: interventions = [] } = useSavInterventions(dossierId);
  const { data: allProfiles = [] } = useProfiles();
  const { data: signedPhotos, isLoading: photosLoading, isError: photosError } = useQuery<{ photos: { path: string; url: string }[] }>({
    queryKey: ['/api/sav', dossierId, 'photos-signed', dossier?.photosReception, interventions.map(i => i.photos).flat().join('|')],
    queryFn: async () => {
      const response = await authFetch(`/api/sav/${dossierId}/photos/signed`);
      if (!response.ok) throw new Error("Impossible de charger les photos SAV");
      return response.json();
    },
    enabled: !!dossier && (!!dossier.photosReception?.length || interventions.some(i => !!i.photos?.length)),
    staleTime: 45 * 60 * 1000,
    refetchInterval: 45 * 60 * 1000,
  });
  const { data: pieces = [] } = useSavPieces(dossierId);
  const updateMutation = useUpdateSavDossier();
  const deleteMutation = useDeleteSavDossier();
  const createInterventionMutation = useCreateSavIntervention();
  const updateInterventionMutation = useUpdateSavIntervention();
  const deleteInterventionMutation = useDeleteSavIntervention();
  const createPieceMutation = useCreateSavPiece();
  const updatePieceMutation = useUpdateSavPiece();
  const deletePieceMutation = useDeleteSavPiece();
  const { toast } = useToast();

  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [showProcessEdit, setShowProcessEdit] = useState(false);
  const [interventionText, setInterventionText] = useState('');
  const [interventionFiles, setInterventionFiles] = useState<File[]>([]);
  const [uploadingIntervention, setUploadingIntervention] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [deleteEntryId, setDeleteEntryId] = useState<string | null>(null);
  const [enlargedPhoto, setEnlargedPhoto] = useState<string | null>(null);
  const [pieceOpen, setPieceOpen] = useState(false);
  const [pieceForm, setPieceForm] = useState({ produitId: "", quantite: "1", note: "" });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [planifLoading, setPlanifLoading] = useState(false);
  const [iaLoading, setIaLoading] = useState(false);
  const [iaError, setIaError] = useState<string | null>(null);
  const [iaCopied, setIaCopied] = useState(false);
  const { profile } = useAuth();

  const handleGenerateResume = async () => {
    setIaLoading(true);
    setIaError(null);
    try {
      const res = await authFetch(`/api/sav/${dossierId}/resume`, { method: "POST" });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || `Erreur ${res.status}`);
      }
      await queryClient.invalidateQueries({ queryKey: ["/api/sav"] });
    } catch (e: any) {
      setIaError("Impossible de générer le résumé. Réessayez.");
    } finally {
      setIaLoading(false);
    }
  };

  const handleCopyResume = async () => {
    if (!(dossier as any)?.resumeIa) return;
    await navigator.clipboard.writeText((dossier as any).resumeIa);
    setIaCopied(true);
    setTimeout(() => setIaCopied(false), 2000);
  };

  const handleDemandePlanification = async () => {
    if (!dossier) return;
    setPlanifLoading(true);
    try {
      const prioriteMap: Record<string, string> = { elevee: "1", moyenne: "2", faible: "3" };
      const taskResp = await authFetch("/api/tasks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          titre: `Planification SAV — ${dossier.reference}`,
          notes: `Demande de planification pour le dossier SAV ${dossier.reference}`,
          priorite: prioriteMap[dossier.priorite] || "2",
          status: "a_faire",
          emetteurType: "libre",
          emetteur: "SAV",
        }),
      });
      if (!taskResp.ok) throw new Error("Erreur lors de la création de la tâche");
      const task = await taskResp.json();
      await updateMutation.mutateAsync({
        id: dossierId,
        demandePlanification: true,
        planificationTaskId: task.id,
      });
      toast({ title: "Planification demandée", description: `Tâche ${task.id.slice(0, 8)}… créée` });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    } finally {
      setPlanifLoading(false);
    }
  };

  if (isLoading || !dossier) return <div className="text-center py-10 text-muted-foreground">Chargement...</div>;

  const client = clients.find((c: any) => c.id === dossier.clientId);
  const tech = technicians.find(t => t.id === dossier.technicienReferentId);

  const openEdit = () => {
    setEditData({
      priorite: dossier.priorite,
      statut: dossier.statut,
      garantie: dossier.garantie,
      dateFinPrevisionnelle: dossier.dateFinPrevisionnelle || "",
      dateResolution: dossier.dateResolution || "",
      technicienReferentId: dossier.technicienReferentId || "",
      notesGenerales: dossier.notesGenerales || "",
      numeroSuiviTransporteur: dossier.numeroSuiviTransporteur || "",
      retraitNecessaire: dossier.retraitNecessaire ?? false,
      deplacementTechnicien: dossier.deplacementTechnicien ?? false,
      robotRemplacementId: dossier.robotRemplacementId || "",
      technicienPartenaire: dossier.technicienPartenaire ?? false,
    });
    setEditOpen(true);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateMutation.mutateAsync({
        id: dossierId,
        priorite: editData.priorite,
        statut: editData.statut,
        garantie: editData.garantie,
        dateFinPrevisionnelle: editData.dateFinPrevisionnelle || null,
        dateResolution: editData.dateResolution || null,
        technicienReferentId: editData.technicienReferentId || null,
        notesGenerales: editData.notesGenerales || null,
        numeroSuiviTransporteur: editData.numeroSuiviTransporteur || null,
        retraitNecessaire: editData.retraitNecessaire ?? false,
        deplacementTechnicien: editData.deplacementTechnicien ?? false,
        robotRemplacementId: editData.robotRemplacementId || null,
        technicienPartenaire: editData.technicienPartenaire ?? false,
      });
      setEditOpen(false);
      toast({ title: "Dossier modifié" });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(dossierId);
      toast({ title: "Dossier supprimé" });
      onBack();
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    }
  };

  const handleCreateIntervention = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!interventionText.trim() && !interventionFiles.length) {
      toast({ variant: "destructive", title: "Ajoutez un texte ou une photo" });
      return;
    }
    setUploadingIntervention(true);
    const paths: string[] = [];
    try {
      for (const file of interventionFiles) {
        const form = new FormData();
        form.append('file', file);
        const response = await authFetch(`/api/sav/${dossierId}/photos?scope=atelier`, { method: "POST", body: form });
        if (!response.ok) throw new Error((await response.json().catch(() => null))?.message || "Impossible d'envoyer la photo");
        paths.push((await response.json()).path);
      }
      await createInterventionMutation.mutateAsync({
        dossierId,
        description: interventionText,
        photos: paths,
      });
      setInterventionText('');
      setInterventionFiles([]);
      queryClient.invalidateQueries({ queryKey: ['/api/sav'] });
      toast({ title: "Entrée ajoutée au suivi" });
    } catch (err: any) {
      if (paths.length) {
        try {
          await authFetch(`/api/sav/${dossierId}/photos/cleanup`, {
            method: 'POST', body: JSON.stringify({ paths }),
          });
        } catch {
          // The original upload error remains the message shown to the user.
        }
      }
      toast({ variant: "destructive", title: "Envoi impossible", description: `${err.message}. Votre texte est conservé.` });
    } finally {
      setUploadingIntervention(false);
    }
  };

  const handleUpdateEntry = async (id: string) => {
    try {
      await updateInterventionMutation.mutateAsync({ dossierId, id, description: editingText });
      setEditingEntryId(null);
      toast({ title: "Entrée modifiée" });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Modification impossible", description: err.message });
    }
  };

  const handleDeleteEntry = async () => {
    if (!deleteEntryId) return;
    try {
      await deleteInterventionMutation.mutateAsync({ dossierId, id: deleteEntryId });
      setDeleteEntryId(null);
      toast({ title: "Entrée supprimée" });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Suppression impossible", description: err.message });
    }
  };

  const handleCreatePiece = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createPieceMutation.mutateAsync({
        dossierId,
        produitId: pieceForm.produitId,
        quantite: parseInt(pieceForm.quantite) || 1,
        note: pieceForm.note || null,
      });
      setPieceOpen(false);
      setPieceForm({ produitId: "", quantite: "1", note: "" });
      toast({ title: "Pièce ajoutée" });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="button-back">
          <ArrowLeft className="w-4 h-4 mr-2" />Retour
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-display font-bold">{dossier.reference}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={prioriteColors[dossier.priorite]}>{prioriteLabels[dossier.priorite]}</Badge>
            <StatusBadge status={dossier.statut} type="ticket" label={statutLabels[dossier.statut]} />
            {dossier.garantie && <Badge variant="outline" className="border-green-500 text-green-600">Garantie</Badge>}
          </div>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={openEdit} data-testid="button-edit-sav"><Pencil className="w-4 h-4 mr-1" />Modifier</Button>
            <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)} data-testid="button-delete-sav"><Trash2 className="w-4 h-4" /></Button>
          </div>
        )}
      </div>

      {/* ── Bloc Résumé IA ── */}
      {(interventions.length + pieces.length >= 2 || (dossier as any).resumeIa) && (
        <div className="rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50/60 dark:bg-violet-950/30 p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-violet-700 dark:text-violet-400 font-semibold text-sm">
              <Sparkles className="w-4 h-4" />
              Résumé généré par IA
              {(dossier as any).resumeIaDate && (
                <span className="text-xs font-normal text-muted-foreground">
                  — Généré le {format(new Date((dossier as any).resumeIaDate), "dd/MM/yyyy à HH:mm", { locale: fr })}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {(dossier as any).resumeIa && (
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={handleCopyResume} data-testid="button-copy-resume-sav">
                  <Copy className="w-3.5 h-3.5 mr-1" />
                  {iaCopied ? "Copié !" : "Copier"}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-3 text-xs border-violet-300 dark:border-violet-700"
                onClick={handleGenerateResume}
                disabled={iaLoading}
                data-testid="button-generate-resume-sav"
              >
                {iaLoading ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Analyse en cours...</>
                ) : (dossier as any).resumeIa ? (
                  <><RefreshCw className="w-3.5 h-3.5 mr-1" />Régénérer</>
                ) : (
                  <><Sparkles className="w-3.5 h-3.5 mr-1" />Générer un résumé</>
                )}
              </Button>
            </div>
          </div>
          {iaError && <p className="text-xs text-destructive">{iaError}</p>}
          {(dossier as any).resumeIa && !iaLoading && (
            <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{(dossier as any).resumeIa}</p>
          )}
        </div>
      )}

      {/* Bloc 1 — Informations générales */}
      <div className="border rounded-lg p-4 space-y-3">
        <h2 className="font-semibold text-lg flex items-center gap-2"><Wrench className="w-5 h-5" />Informations générales</h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-muted-foreground">Référence :</span> <span className="font-mono">{dossier.reference}</span></div>
          <div><span className="text-muted-foreground">Origine :</span> {dossier.origine === 'demo' ? 'Robot démo' : 'Robot client'}</div>
          <div><span className="text-muted-foreground">Robot :</span> {savRobotLabel(dossier as SavDossierDisplay, robots)}</div>
          <div><span className="text-muted-foreground">Client :</span> {(client as any)?.nomCommercial || client?.companyName || '—'}</div>
          <div><span className="text-muted-foreground">Garantie :</span> {dossier.garantie ? 'Oui' : 'Non'}</div>
          <div><span className="text-muted-foreground">Technicien référent :</span> {tech ? `${tech.firstName} ${tech.lastName}` : '—'}</div>
          <div><span className="text-muted-foreground">Date création :</span> {dossier.dateCreation}</div>
          <div><span className="text-muted-foreground">Fin prévisionnelle :</span> {dossier.dateFinPrevisionnelle || '—'}</div>
          <div><span className="text-muted-foreground">Date résolution :</span> {dossier.dateResolution || '—'}</div>
          <div><span className="text-muted-foreground">Suivi transporteur :</span> {dossier.numeroSuiviTransporteur || '—'}</div>
        </div>
        <div className="text-sm">
          <span className="text-muted-foreground">Problématique :</span>
          <p className="mt-1 bg-muted/30 rounded p-2" onCopy={handlePlainTextCopy}>{dossier.problematique}</p>
        </div>
        {dossier.notesGenerales && (
          <div className="text-sm">
            <span className="text-muted-foreground">Notes :</span>
            <p className="mt-1">{dossier.notesGenerales}</p>
          </div>
        )}
      </div>

      {/* Bloc — Process de traitement */}
      {(() => {
        const remplacementRobot = robots.find(r => r.id === dossier.robotRemplacementId);
        const anyProcess = dossier.retraitNecessaire || dossier.deplacementTechnicien || dossier.technicienPartenaire || dossier.robotRemplacementId || dossier.demandePlanification;
        return (
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-lg flex items-center gap-2"><Settings2 className="w-5 h-5" />Process de traitement</h2>
              {canEdit && !dossier.demandePlanification && (
                <Button size="sm" variant="outline" onClick={handleDemandePlanification} disabled={planifLoading} data-testid="button-demande-planif">
                  <ClipboardList className="w-4 h-4 mr-1" />
                  {planifLoading ? "Envoi…" : "Demander une planification"}
                </Button>
              )}
              {dossier.demandePlanification && (
                <Badge className="bg-blue-100 text-blue-700 border-blue-300 gap-1">
                  <CheckCircle2 className="w-3 h-3" />Planification demandée
                </Badge>
              )}
            </div>
            {!anyProcess ? (
              <p className="text-sm text-muted-foreground italic">Aucun process de traitement défini.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${dossier.retraitNecessaire ? 'bg-orange-500' : 'bg-muted'}`} />
                  <span className={dossier.retraitNecessaire ? 'font-medium' : 'text-muted-foreground'}>Retrait nécessaire</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${dossier.deplacementTechnicien ? 'bg-blue-500' : 'bg-muted'}`} />
                  <span className={dossier.deplacementTechnicien ? 'font-medium' : 'text-muted-foreground'}>Déplacement technicien</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${dossier.technicienPartenaire ? 'bg-purple-500' : 'bg-muted'}`} />
                  <span className={dossier.technicienPartenaire ? 'font-medium' : 'text-muted-foreground'}>Technicien partenaire</span>
                </div>
                {remplacementRobot && (
                  <div className="flex items-center gap-2 col-span-2">
                    <Bot className="w-4 h-4 text-green-600" />
                    <span className="font-medium">Robot de remplacement :</span>
                    <span>{remplacementRobot.modelName} — {remplacementRobot.nickname || remplacementRobot.serialNumber}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Bloc 2 — Photos à réception */}
      <div className="border rounded-lg p-4 space-y-3">
        <h2 className="font-semibold text-lg flex items-center gap-2"><Camera className="w-5 h-5" />Photos à réception</h2>
        {dossier.photosReception && dossier.photosReception.length > 0 ? (
          photosError ? <p className="text-sm text-destructive">Impossible de charger les photos SAV.</p> :
          photosLoading || !signedPhotos ? <p className="text-sm text-muted-foreground">Chargement des photos…</p> :
          <div className="grid grid-cols-4 gap-2">
            {signedPhotos.photos.filter(photo => dossier.photosReception?.includes(photo.path)).map(({ path, url }, i) => (
              <img key={path} src={url} alt={`Photo ${i + 1}`} className="rounded border object-cover aspect-square" />
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Aucune photo</p>
        )}
        {canEdit && (
          <PhotoUploader dossierId={dossierId} />
        )}
      </div>

      {/* Bloc 3 — Historique des interventions */}
      <div className="border rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-lg flex items-center gap-2"><Clock className="w-5 h-5" />Suivi d'atelier ({interventions.length})</h2>
        </div>
        {dossier.statut !== 'cloture' && (
          <form onSubmit={handleCreateIntervention} className="space-y-2 border rounded-lg p-3">
            <Label htmlFor="sav-suivi-text">Nouvelle entrée</Label>
            <Textarea id="sav-suivi-text" data-testid="input-intervention-desc" value={interventionText} onChange={e => setInterventionText(e.target.value)} placeholder="Décrivez l'avancement de la réparation…" />
            <div className="flex items-center gap-2 flex-wrap">
              <label className="inline-flex items-center gap-2 cursor-pointer text-sm rounded-md border px-3 py-2 hover:bg-muted">
                <Camera className="w-4 h-4" /> Photo
                <input type="file" accept="image/*" capture="environment" multiple className="sr-only" onChange={e => {
                  const files = Array.from(e.target.files || []);
                  if (files.some(file => file.size > 50_000_000)) {
                    toast({ variant: "destructive", title: "Photo trop volumineuse", description: "Chaque photo doit faire 50 Mo maximum." });
                  } else {
                    setInterventionFiles(current => [...current, ...files]);
                  }
                  e.target.value = '';
                }} />
              </label>
              {!!interventionFiles.length && <span className="text-sm text-muted-foreground">{interventionFiles.length} photo(s) · {interventionFiles.map(f => f.name).join(', ')} <button type="button" className="underline" onClick={() => setInterventionFiles([])}>Retirer</button></span>}
              <Button type="submit" disabled={uploadingIntervention || (!interventionText.trim() && !interventionFiles.length)} data-testid="button-send-intervention">
                {uploadingIntervention ? "Envoi…" : "Envoyer"}
              </Button>
            </div>
          </form>
        )}
        {interventions.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune entrée de suivi</p>
        ) : (
          <div className="space-y-4">
            {interventions.map((int: SavIntervention) => {
              const intTech = allProfiles.find(t => t.id === int.technicienId);
              const edited = int.description.startsWith(EDITED_PREFIX);
              return (
                <div key={int.id} className="border-l-2 border-primary/30 pl-4 space-y-1" data-testid={`intervention-${int.id}`}>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-3 h-3 text-muted-foreground" />
                    <span className="font-medium">{int.dateHeure ? format(new Date(int.dateHeure), "dd/MM/yyyy HH:mm", { locale: fr }) : '—'}</span>
                    <span className="text-muted-foreground">—</span>
                    <span className="font-medium">{intTech ? `${intTech.firstName} ${intTech.lastName}` : 'Auteur inconnu'}</span>
                    {edited && <span className="text-xs text-muted-foreground">· modifié</span>}
                  </div>
                  {editingEntryId === int.id ? (
                    <div className="space-y-2">
                      <Textarea value={editingText} onChange={e => setEditingText(e.target.value)} aria-label="Modifier l'entrée" />
                      <Button size="sm" onClick={() => handleUpdateEntry(int.id)} disabled={updateInterventionMutation.isPending}>Enregistrer</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingEntryId(null)}>Annuler</Button>
                    </div>
                  ) : int.description.replace(EDITED_PREFIX, '').trim() ? <p className="text-sm whitespace-pre-wrap">{int.description.replace(EDITED_PREFIX, '')}</p> : null}
                  {int.photos && int.photos.length > 0 && (
                    <div className="flex gap-2 mt-1">
                      {int.photos.map((path, i) => (
                        signedPhotos?.photos.find(photo => photo.path === path) && <button key={path} type="button" onClick={() => setEnlargedPhoto(signedPhotos.photos.find(photo => photo.path === path)!.url)} aria-label={`Agrandir la photo ${i + 1}`}>
                          <img src={signedPhotos.photos.find(photo => photo.path === path)!.url} alt={`Photo de suivi ${i + 1}`} className="w-16 h-16 rounded border object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                  {int.photos?.length && (photosError || photosLoading) ? <p className="text-xs text-muted-foreground">{photosError ? "Impossible de charger les photos" : "Chargement des photos…"}</p> : null}
                  {profile?.id === int.technicienId && (
                    <div className="flex gap-2">
                      {dossier.statut !== 'cloture' && <Button size="sm" variant="ghost" onClick={() => { setEditingEntryId(int.id); setEditingText(int.description.replace(EDITED_PREFIX, '').trim()); }}>Modifier</Button>}
                      <Button size="sm" variant="ghost" onClick={() => setDeleteEntryId(int.id)}>Supprimer</Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <Dialog open={!!deleteEntryId} onOpenChange={open => { if (!open) setDeleteEntryId(null); }}>
          <DialogContent><DialogHeader><DialogTitle>Supprimer cette entrée ?</DialogTitle><DialogDescription>Ses photos seront également supprimées. Cette action est définitive.</DialogDescription></DialogHeader>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setDeleteEntryId(null)}>Annuler</Button><Button variant="destructive" onClick={handleDeleteEntry} disabled={deleteInterventionMutation.isPending}>Supprimer</Button></div>
          </DialogContent>
        </Dialog>
        <Dialog open={!!enlargedPhoto} onOpenChange={open => { if (!open) setEnlargedPhoto(null); }}>
          <DialogContent className="max-w-4xl"><DialogHeader><DialogTitle>Photo du suivi</DialogTitle></DialogHeader>{enlargedPhoto && <img src={enlargedPhoto} alt="Photo de suivi agrandie" className="max-h-[75vh] max-w-full object-contain mx-auto" />}</DialogContent>
        </Dialog>
      </div>

      {/* Bloc 4 — Pièces nécessaires */}
      <div className="border rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-lg flex items-center gap-2"><Package className="w-5 h-5" />Pièces nécessaires ({pieces.length})</h2>
          {canEdit && (
            <Dialog open={pieceOpen} onOpenChange={setPieceOpen}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="button-add-piece"><Plus className="w-4 h-4 mr-1" />Ajouter une pièce</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Ajouter une pièce</DialogTitle>
                  <DialogDescription>Sélectionner depuis le référentiel produits</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreatePiece} className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label>Produit *</Label>
                    <SearchSelect
                      value={pieceForm.produitId || null}
                      onValueChange={v => setPieceForm({ ...pieceForm, produitId: v || "" })}
                      options={products.filter(p => p.actif).map(p => ({
                        value: p.id,
                        label: p.nomFr,
                        sublabel: p.sku,
                        imageUrl: p.imageUrl || undefined,
                      }))}
                      placeholder="Sélectionner un produit"
                      emptyText="Aucun produit trouvé"
                      data-testid="select-piece-product"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Quantité</Label>
                      <Input type="number" min="1" value={pieceForm.quantite} onChange={e => setPieceForm({ ...pieceForm, quantite: e.target.value })} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Note</Label>
                    <Input value={pieceForm.note} onChange={e => setPieceForm({ ...pieceForm, note: e.target.value })} placeholder="Optionnel" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" type="button" onClick={() => setPieceOpen(false)}>Annuler</Button>
                    <Button type="submit" disabled={createPieceMutation.isPending}>Ajouter</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
        {pieces.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune pièce enregistrée</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Produit</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Qté</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Note</TableHead>
                {canEdit && <TableHead></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {pieces.map((p: SavPiece) => {
                const prod = products.find(pr => pr.id === p.produitId);
                return (
                  <TableRow key={p.id} data-testid={`piece-${p.id}`}>
                    <TableCell className="flex items-center gap-2">
                      {prod?.imageUrl && <img src={prod.imageUrl} alt="" className="w-8 h-8 rounded border object-cover" />}
                      {prod?.nomFr || '—'}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{prod?.sku || '—'}</TableCell>
                    <TableCell>{p.quantite}</TableCell>
                    <TableCell>
                      {canEdit ? (
                        <Select value={p.statut} onValueChange={v => updatePieceMutation.mutate({ id: p.id, statut: v })}>
                          <SelectTrigger className="w-[140px] h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(pieceStatutLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : pieceStatutLabels[p.statut]}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{p.note || '—'}</TableCell>
                    {canEdit && (
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => deletePieceMutation.mutate(p.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Modifier le dossier</DialogTitle>
            <DialogDescription>{dossier.reference}</DialogDescription>
          </DialogHeader>
          {editData && (
            <form onSubmit={handleEdit} className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Statut</Label>
                  <Select value={editData.statut} onValueChange={v => setEditData({ ...editData, statut: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(statutLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priorité</Label>
                  <Select value={editData.priorite} onValueChange={v => setEditData({ ...editData, priorite: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(prioriteLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Technicien référent</Label>
                <SearchSelect
                  value={editData.technicienReferentId || null}
                  onValueChange={v => setEditData({ ...editData, technicienReferentId: v || "" })}
                  options={technicians.map(t => ({
                    value: t.id,
                    label: `${t.firstName} ${t.lastName}`,
                  }))}
                  placeholder="Sélectionner"
                  emptyText="Aucun technicien trouvé"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fin prévisionnelle</Label>
                  <Input type="date" value={editData.dateFinPrevisionnelle} onChange={e => setEditData({ ...editData, dateFinPrevisionnelle: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Date résolution</Label>
                  <Input type="date" value={editData.dateResolution} onChange={e => setEditData({ ...editData, dateResolution: e.target.value })} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="edit-garantie" checked={editData.garantie} onCheckedChange={c => setEditData({ ...editData, garantie: c === true })} />
                <Label htmlFor="edit-garantie" className="cursor-pointer">Sous garantie</Label>
              </div>
              <div className="space-y-2">
                <Label>N° suivi transporteur</Label>
                <Input value={editData.numeroSuiviTransporteur} onChange={e => setEditData({ ...editData, numeroSuiviTransporteur: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Notes générales</Label>
                <Textarea value={editData.notesGenerales} onChange={e => setEditData({ ...editData, notesGenerales: e.target.value })} />
              </div>

              {/* Process de traitement */}
              <div className="border rounded-lg overflow-hidden">
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-4 py-3 bg-muted/40 hover:bg-muted/60 transition-colors text-sm font-medium"
                  onClick={() => setShowProcessEdit(!showProcessEdit)}
                >
                  <span className="flex items-center gap-2"><Settings2 className="w-4 h-4" />Process de traitement</span>
                  {showProcessEdit ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showProcessEdit && (
                  <div className="p-4 space-y-3 border-t">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2">
                        <Checkbox id="e-retrait" checked={editData.retraitNecessaire} onCheckedChange={c => setEditData({ ...editData, retraitNecessaire: c === true })} />
                        <Label htmlFor="e-retrait" className="cursor-pointer text-sm">Retrait nécessaire</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="e-deplacement" checked={editData.deplacementTechnicien} onCheckedChange={c => setEditData({ ...editData, deplacementTechnicien: c === true })} />
                        <Label htmlFor="e-deplacement" className="cursor-pointer text-sm">Déplacement technicien</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="e-partenaire" checked={editData.technicienPartenaire} onCheckedChange={c => setEditData({ ...editData, technicienPartenaire: c === true })} />
                        <Label htmlFor="e-partenaire" className="cursor-pointer text-sm">Technicien partenaire</Label>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm">Robot de remplacement</Label>
                      <SearchSelect
                        value={editData.robotRemplacementId || null}
                        onValueChange={v => setEditData({ ...editData, robotRemplacementId: v || "" })}
                        options={robots.filter(r => r.robotType === 'demo' && (isRobotFleetAvailable(r.status) || r.id === dossier.robotRemplacementId)).map(r => ({
                          value: r.id,
                          label: r.modelName,
                          sublabel: [r.serialNumber, r.nickname].filter(Boolean).join(" · "),
                        }))}
                        placeholder="Aucun robot de remplacement"
                        emptyText="Aucun robot disponible"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" type="button" onClick={() => setEditOpen(false)}>Annuler</Button>
                <Button type="submit" disabled={updateMutation.isPending}>Enregistrer</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer le dossier</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer le dossier <strong>{dossier.reference}</strong> ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Annuler</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>Supprimer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhotoUploader({ dossierId }: { dossierId: string }) {
  const { toast } = useToast();
  const updateMutation = useUpdateSavDossier();
  const { data: dossier } = useSavDossier(dossierId);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    if (Array.from(files).some(file => file.size > 50_000_000)) {
      toast({ variant: "destructive", title: "Photo trop volumineuse", description: "Chaque photo doit faire 50 Mo maximum." });
      e.target.value = "";
      return;
    }
    try {
      const paths: string[] = [];
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append('file', file);
        const token = supabase.auth.getSession().then(s => s.data.session?.access_token);
        const res = await fetch(`/api/sav/${dossierId}/photos`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${await token}` },
          body: formData,
        });
        if (!res.ok) {
          const error = await res.json().catch(() => null);
          throw new Error(error?.message || "Échec de l'envoi de la photo");
        }
        const data = await res.json();
        paths.push(data.path);
      }
      const existingPhotos = dossier?.photosReception || [];
      await updateMutation.mutateAsync({
        id: dossierId,
        photosReception: [...existingPhotos, ...paths],
      });
      toast({ title: `${paths.length} photo(s) uploadée(s)` });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur upload", description: err.message });
    }
    e.target.value = "";
  };

  return (
    <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-primary hover:underline">
      <Upload className="w-4 h-4" />
      Ajouter des photos
      <input type="file" multiple accept="image/*" className="hidden" onChange={handleUpload} />
    </label>
  );
}
